import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import {
  LTD_SLOT_LINE_PAINT,
  LTD_SLOT_WIDTH_EDITING,
  LTD_SLOT_WIDTH_VIEWING,
} from 'components/map/ltd-slot-paint';
import { formatMutationResource } from 'utils/misc';
import {
  compositeRefreshTriggerAtom,
  geometryRefreshAtom,
  ltdPropertiesVisibilityAtom,
} from './atoms';
import { mapSlotGeomAtoms } from './inventory-surface/atoms';
import {
  tcInventoryGunBarrelScopeAtom,
  tcHighlightedFormationAtom,
  tcInventoryFoucsGateAtom,
} from './type-curve-filters/atoms';
import {
  ltdLateralOverridesAtom,
  selectedLtdSlotIdsAtom,
} from 'pages/inventory-workspace/unit-builder/atoms';
import { lateralLengthFt } from 'pages/inventory-workspace/unit-builder/ltd-lateral-trim';
import type { UnitFormationSlotResult } from './types';
import { TcCompositeTangentTooltip } from './tc-composite-tangent-tooltip';

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

export const TcSlotGeometriesLayer = ({
  typeCurveHeaderDetailsId,
  unitId,
  slotsAtom = mapSlotGeomAtoms.main,
  formationNameOverride,
  packageIdOverride,
}: {
  /** Type Curve page scope. Unused in inventory mode (scoped by workspace). */
  typeCurveHeaderDetailsId?: number;
  /** Inventory Workspace: scope the fetch to a single unit (the Unit Builder). */
  unitId?: number | null;
  /**
   * Which mutation atom holds this map's slot copy. The Unit Builder map passes
   * `mapSlotGeomAtoms.builder` so its unit-scoped fetch + trim edits stay separate
   * from the main-page map's `mapSlotGeomAtoms.main` (default) — two independent
   * instances, no sharing.
   */
  slotsAtom?: typeof mapSlotGeomAtoms.main;
  /**
   * Formation + zone to scope by, decoupled from the main page. The Unit Builder
   * map passes its OWN arrow-stepped band here (`builderSelectedFormationAtom`) so
   * stepping refetches this map's slots without touching `tcHighlightedFormationAtom`.
   * When omitted (main map), the layer falls back to those main-page atoms. `null`
   * is a valid override (builder band not yet resolved) — distinct from omitted.
   */
  formationNameOverride?: string | null;
  packageIdOverride?: number | null;
}) => {
  // Builder scope: only the Unit Builder map (a single unit in view) applies the
  // trim overlay + recenter. The main map shows the initial saved geometry.
  const isBuilder = typeof unitId === 'number';
  // Layering note: the builder owns this selection, and this shared layer has to
  // know it to avoid double-drawing the lateral the builder emphasises.
  const selectedLtdIds = useAtomValue(selectedLtdSlotIdsAtom);
  // Both inventory actions refetch every map's slot geometry on BOTH surfaces —
  // Update Geometries persists the edit, so the main map should reflect it too
  // (not stay on the pre-edit shape). `geometryRefreshAtom` bumps on either.
  const refreshKey = useAtomValue(geometryRefreshAtom);
  const compositeRefresh = useAtomValue(compositeRefreshTriggerAtom);
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const highlightedFormation = useAtomValue(tcHighlightedFormationAtom);
  // The formation + zone this map scopes by. An explicit override (the builder
  // map's own band) wins; otherwise the main-page selection.
  const effFormation =
    formationNameOverride !== undefined
      ? formationNameOverride
      : highlightedFormation;
  const effPackageId =
    packageIdOverride !== undefined
      ? packageIdOverride
      : (inventoryScope?.inventoryFormationPackageId ?? null);
  // Inventory Workspace only: hold the slots query until the FOUCS exclusions
  // recompute returned 200. `null` on plain Type Curve pages (never blocked).
  const foucsGate = useAtomValue(tcInventoryFoucsGateAtom);
  const foucsBlocked = foucsGate !== null && foucsGate !== 'ready';
  const [slotsQuery, runSlotsQuery] = useAtom(slotsAtom);
  useEffect(() => {
    if (foucsBlocked) return;
    // On an Inventory Workspace scope by the workspace: its bridge leaves the header detail null, so
    // a header-detail scope returns nothing. The plain Type Curve page scopes by header detail.
    // A selected formation (Inventory Workspace only) narrows the query server-side to that
    // formation's slots, mirroring the development-areas call; `highlightedFormation` is null on the
    // plain Type Curve page, so it sends no formation filter there.
    const query = {
      ...(inventoryScope
        ? { inventoryWorkspaceId: inventoryScope.inventoryWorkspaceId }
        : { typeCurveHeaderDetailsId }),
      wellStatus: 'LTD',
      ...(effFormation ? { formationName: effFormation } : {}),
      // The map slot layer scopes to the SELECTED zone only — not every
      // origin-area zone the gun barrel spans. `formationName` above narrows to
      // the same zone's formation, so the two stay consistent.
      ...(effPackageId ? { inventoryFormationPackageIds: [effPackageId] } : {}),
      // Unit Builder: scope to the open unit rather than the whole workspace.
      ...(typeof unitId === 'number' ? { unitId } : {}),
    };
    runSlotsQuery({
      url: '/queries/inventory_unit_slots',
      type: 'POST',
      data: { query },
    });
  }, [
    typeCurveHeaderDetailsId,
    unitId,
    inventoryScope,
    effFormation,
    effPackageId,
    refreshKey,
    compositeRefresh,
    runSlotsQuery,
    foucsBlocked,
  ]);

  const { data: slotsData } = formatMutationResource(slotsQuery);
  // Front-end trim overlay (Unit Builder). Empty on the Type Curve pages, where
  // no trimming happens. Substituted per slot so a trimmed-but-unselected LTD
  // stays trimmed in the base line, not just under the selected-lateral grips.
  const ltdOverrides = useAtomValue(ltdLateralOverridesAtom);
  const ltdVisible = useAtomValue(ltdPropertiesVisibilityAtom);

  // The server LTD geometry is drawn as-is: it already sits inside the unit's true
  // footprint (which the Unit Builder now seeds the editable boundary from). An
  // earlier client re-centre slid the group onto the cross-section axis to fit the
  // frame-derived RECTANGLE boundary; against the true boundary that shift is
  // spurious and pushes fixed laterals outside the unit as it is reshaped, so it is
  // gone. Only the front-end trim overlay may replace a slot's line.
  // A composite leg's stick (start → end) from the composite object, which
  // carries every leg whatever formation the rows are filtered to (contract).
  const compositeLegSticks = useMemo(() => {
    const sticks = new Map<number, GeoJSON.Position[]>();
    for (const c of slotsData?.composites ?? []) {
      for (const leg of c.legs ?? []) {
        const start = leg.ends?.find((e) => e.end === 'start')?.coordinates;
        const end = leg.ends?.find((e) => e.end === 'end')?.coordinates;
        if (start && end) sticks.set(leg.slotId, [start, end]);
      }
    }
    return sticks;
  }, [slotsData]);

  const featureCollection = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!slotsData?.results) return EMPTY_FC;

    const features: GeoJSON.Feature[] = slotsData.results
      .filter((r: UnitFormationSlotResult) => {
        if (!r.geometryGeojson || r.geometryGeojson.type !== 'LineString')
          return false;
        // Hide geometry for slots that have been excluded (valued: false).
        // Optimistic override takes precedence over the API response value.
        if (r.wellStatus !== 'LTD') return false;
        // A composite leg is drawn with its composite below.
        if (compositeLegSticks.has(r.id)) return false;
        // A selected lateral is drawn by `TcLateralTrimLayer` instead, so skip it
        // here — otherwise both lines paint and the aqua base shows through the
        // gaps of the darker, wider selected dash, reading as a doubled line
        // rather than one emphasised one.
        //
        // The condition mirrors what that layer actually draws (it skips
        // `valued: false`), so a selected-but-unvalued lateral still gets its base
        // line here and cannot disappear.
        if (isBuilder && selectedLtdIds.has(r.id) && r.valued !== false) {
          return false;
        }
        // Inventory Workspace: show only the scoped formation's slot lines (the
        // main map's tree leaf, or the builder map's stepped band). Not applied on
        // the Type Curve pages (inventoryScope null).
        if (inventoryScope && effFormation && r.formationName !== effFormation)
          return false;
        return true;
      })
      .map((r: UnitFormationSlotResult) => ({
        type: 'Feature' as const,
        geometry:
          isBuilder && ltdOverrides.get(r.id)
            ? {
                type: 'LineString',
                coordinates: ltdOverrides.get(r.id) as GeoJSON.Position[],
              }
            : {
                type: 'LineString',
                coordinates: (r.geometryGeojson as GeoJSON.LineString)
                  .coordinates,
              },
        properties: {
          id: r.id,
          unitName: r.unitName,
          formationName: r.formationName,
          slotNumber: r.slotNumber,
          hasWell: r.hasWell,
          uwi: r.uwi,
          wellProportionInUnit: r.wellProportionInUnit,
          wellStatus: r.wellStatus,
          forecastStatus: r.forecastStatus,
          reserveCategory: r.reserveCategory,
        },
      }));

    return { type: 'FeatureCollection', features };
  }, [
    slotsData,
    inventoryScope,
    effFormation,
    ltdOverrides,
    isBuilder,
    selectedLtdIds,
    compositeLegSticks,
  ]);

  // Composite wellbores, per the contract's map rule: every leg (any formation)
  // and the dashed tangents come from the composite. In the builder a leg and
  // its tangent follow unsaved trims (mock), and a selected leg is left to
  // `TcLateralTrimLayer`, as for any lateral.
  const compositeCollection = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = [];
    const seen = new Set<number>();
    const xy = (p: GeoJSON.Position): GeoJSON.Position => [p[0], p[1]];
    const rows = new Map<number, UnitFormationSlotResult>(
      (slotsData?.results ?? []).map((r: UnitFormationSlotResult) => [r.id, r])
    );
    const trimOf = (slotId: number) => {
      return isBuilder ? ltdOverrides.get(slotId) : undefined;
    };
    // A leg's current tip and its depth, or null when its stick isn't loaded.
    const legTip = (slotId: number, end: 'start' | 'end' | undefined) => {
      const stick = compositeLegSticks.get(slotId);
      if (!end || !stick) return null;
      const coords = trimOf(slotId) ?? stick;
      const tip = end === 'start' ? coords[0] : coords[coords.length - 1];
      return { at: xy(tip), z: stick[0][2] };
    };
    for (const c of slotsData?.composites ?? []) {
      if (c.id === null || seen.has(c.id)) continue;
      seen.add(c.id);
      const props = { compositeId: c.id, name: c.name };
      const legOf = (slotId: number) =>
        (c.legs ?? []).find((l) => l.slotId === slotId);
      for (const leg of c.legs ?? []) {
        const stick = compositeLegSticks.get(leg.slotId);
        if (!stick) continue;
        // Mirrors the row rule above: the trim layer draws a selected leg
        // when its row is loaded and valued.
        const row = rows.get(leg.slotId);
        if (
          isBuilder &&
          selectedLtdIds.has(leg.slotId) &&
          row?.geometryGeojson?.type === 'LineString' &&
          row.valued !== false
        ) {
          continue;
        }
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: (trimOf(leg.slotId) ?? stick).map(xy),
          },
          properties: {
            ...props,
            kind: 'leg',
            id: leg.slotId,
            formationName: leg.formationName,
          },
        });
      }
      (c.tangentsGeojson?.coordinates ?? []).forEach((tangent, i) => {
        const t = c.tangents[i];
        let coordinates = tangent.map(xy);
        let lengthFt = t?.lengthFt ?? null;
        if (t && (trimOf(t.fromSlotId) || trimOf(t.toSlotId))) {
          const from = legTip(t.fromSlotId, legOf(t.fromSlotId)?.exitEnd);
          const to = legTip(t.toSlotId, legOf(t.toSlotId)?.entryEnd);
          const last = tangent.length - 1;
          coordinates = [
            from?.at ?? coordinates[0],
            to?.at ?? coordinates[last],
          ];
          // Depth included, as the server measures it.
          const dz = (from?.z ?? tangent[0][2]) - (to?.z ?? tangent[last][2]);
          const flat = lateralLengthFt(coordinates);
          lengthFt = Number.isFinite(dz) ? Math.hypot(flat, dz) : flat;
        }
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates },
          properties: { ...props, kind: 'tangent', lengthFt },
        });
      });
    }
    return { type: 'FeatureCollection', features };
  }, [slotsData, isBuilder, ltdOverrides, selectedLtdIds, compositeLegSticks]);
  const compositeVisibility = ltdVisible ? 'visible' : 'none';

  return (
    <LayerBand band={LAYER_ORDER.unitLaterals}>
      <Source id="tc-slot-geometries" type="geojson" data={featureCollection}>
        <Layer
          id="tc-slot-geometries-layer"
          type="line"
          // Driven by the map's "LTD Properties" toggle. Hidden via layout
          // rather than by unmounting, so the source and its fetch survive a
          // toggle and the layer keeps its place in the band ordering.
          layout={{ visibility: ltdVisible ? 'visible' : 'none' }}
          // Shared with the Add Wellbore picker, so the same stick reads the same
          // on every map that draws it. The shade's reasoning lives with the
          // constant. No separate scoped-formation tone: in inventory scope the
          // filter above renders only the scoped formation's lines, so there is
          // nothing to tell apart by colour.
          paint={{
            ...LTD_SLOT_LINE_PAINT,
            // Heavier in the builder, where the laterals are the thing being
            // edited; the workspace map keeps the lighter weight it had.
            'line-width': isBuilder
              ? LTD_SLOT_WIDTH_EDITING
              : LTD_SLOT_WIDTH_VIEWING,
          }}
        />
      </Source>
      <Source id="tc-slot-composites" type="geojson" data={compositeCollection}>
        <Layer
          id="tc-slot-composites-line"
          type="line"
          layout={{ visibility: compositeVisibility }}
          paint={{
            'line-color': '#009d9a',
            'line-width': isBuilder ? 2.5 : 2,
            'line-opacity': 1,
            'line-dasharray': [4, 3],
          }}
        />
      </Source>
      <TcCompositeTangentTooltip layerId="tc-slot-composites-line" />
    </LayerBand>
  );
};

TcSlotGeometriesLayer.displayName = 'TcSlotGeometriesLayer';
